import type { ExtractionStage } from '../../../api/listeners/document-extraction'

/** Calendar components only: interpreting a birthday as a UTC timestamp can change its day. */
export const normalizeExtractedBirthDate = (value: string, today = new Date()): string | null => {
    const text = value.trim()
    const printed = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(text)
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text)
    if (!printed && !iso) return null
    const [year, month, day] = printed ? [+printed[3], +printed[2], +printed[1]] : [+iso![1], +iso![2], +iso![3]]
    const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
    const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
    if (year < 100 || month < 1 || month > 12 || day < 1 || day > days[month - 1]) return null
    if (year * 10000 + month * 100 + day > today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate()) return null
    return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${String(year).padStart(4, '0')}`
}

export const recognizedBirthDates = (stage: ExtractionStage | null): { dates: string[]; needsReview: boolean } => {
    const dates: string[] = []
    let needsReview = false
    if (!stage || !['succeeded', 'failed'].includes(stage.status)) return { dates, needsReview }
    for (const part of stage.parts) {
        if (part.status !== 'succeeded' || !part.result) continue
        for (const document of part.result.documents) {
            if (!['passport_main', 'snils'].includes(document.type)) continue
            const field = document.fields.find(item => item.name === 'birthDate')
            const rawText = field?.rawValue?.trim() || ''
            const normalizedText = field?.normalizedValue?.trim() || ''
            if (!field || (!rawText && !normalizedText)) continue
            const raw = rawText ? normalizeExtractedBirthDate(rawText) : null
            const normalized = normalizedText ? normalizeExtractedBirthDate(normalizedText) : null
            const date = normalized || raw
            const conflicting = part.result.conflicts.some(conflict => conflict.code === 'birth_date_mismatch'
                || conflict.fields.some(ref => ref.documentId === document.documentId && ref.field === 'birthDate'))
                || part.result.unresolved.some(item => item.code === 'birth_date_mismatch'
                    || (item.documentId === document.documentId && item.field === 'birthDate'))
            // A textual printed month can legitimately be normalized by LMS. An
            // invalid/incomplete numeric date must not be silently repaired.
            const invalidPrintedDate = rawText && !raw && /^[\d\s./-]+$/.test(rawText)
            if (!date || field.status !== 'extracted' || field.candidates.length || invalidPrintedDate
                || ['failed', 'invalid', 'conflict'].includes(field.validation.format)
                || ['failed', 'invalid', 'conflict'].includes(field.validation.crossDocument)
                || (normalizedText && !normalized) || (raw && normalized && raw !== normalized)) {
                needsReview = true
                continue
            }
            if (conflicting) needsReview = true
            if (!dates.includes(date)) dates.push(date)
        }
    }
    return { dates, needsReview }
}
